// RTL language detection and direction utilities

const RTL_LANGUAGES = ['ar', 'fa', 'ur', 'he'];

/**
 * Check if a language code represents a right-to-left language
 * @param languageCode - The language code to check (e.g., 'ar', 'en', 'fa')
 * @returns true if the language is RTL, false otherwise
 */
export const isRTL = (languageCode: string): boolean => {
  return RTL_LANGUAGES.includes(languageCode);
};

/**
 * Get the text direction for a given language
 * @param languageCode - The language code to check
 * @returns 'rtl' for RTL languages, 'ltr' for LTR languages
 */
export const getTextDirection = (languageCode: string): 'ltr' | 'rtl' => {
  return isRTL(languageCode) ? 'rtl' : 'ltr';
};

/**
 * Get conditional CSS classes for RTL support
 * @param languageCode - The language code to check
 * @returns object with direction and alignment classes
 */
export const getRTLClasses = (languageCode: string) => {
  const isRTLLang = isRTL(languageCode);
  return {
    direction: isRTLLang ? 'rtl' : 'ltr',
    textAlign: isRTLLang ? 'text-right' : 'text-left',
    dirAttribute: isRTLLang ? 'rtl' : 'ltr',
    className: isRTLLang ? 'dir-rtl text-right' : 'dir-ltr text-left'
  };
};

/**
 * Get dir attribute value for HTML elements
 * @param languageCode - The language code to check
 * @returns 'rtl' or 'ltr' for the dir attribute
 */
export const getDirAttribute = (languageCode: string): 'ltr' | 'rtl' => {
  return getTextDirection(languageCode);
}; 